import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@material-ui/icons/Close';

/**
 * Generic floating "nudge" — a dark speech-bubble tooltip anchored to the RIGHT
 * of a target element. Reusable anywhere: pass `anchorSelector` (a CSS selector
 * for the element to sit beside) OR a fixed `anchorRect`, plus the content as
 * children. Optional close (X) button.
 *
 * Behaviour mirrors the original Classes nudge exactly — fixed position beside
 * the anchor, slide-in animation, orange accent bar, left-pointing arrow, and
 * click-to-dismiss — so existing usages look/behave identically.
 *
 * Props:
 *   anchorSelector {string}   CSS selector of the element to anchor to (polled).
 *   anchorRect     {DOMRect}  Alternatively, a fixed rect (skips DOM lookup).
 *   children       {node}     Content (string or JSX).
 *   onDismiss      {fn}       Called on close / click-to-dismiss.
 *   showClose      {bool}     Show the X button (default true).
 *   dismissOnClick {bool}     Whole bubble dismisses on click (default true).
 *   placement      {string}   'right' (default, arrow on left) or 'left' (arrow on
 *                             right) — use 'left' when the anchor is near the right
 *                             edge so the bubble opens inward.
 *   width, offset, zIndex     Layout knobs with sensible defaults.
 *   sx             {object}   Extra styles merged onto the bubble.
 */
const FloatingNudge = ({
    anchorSelector,
    anchorRect = null,
    children,
    onDismiss,
    showClose = true,
    dismissOnClick = true,
    placement = 'right',
    width = 280,
    offset = 12,
    zIndex = 9999,
    sx = {},
}) => {
    const [rect, setRect] = useState(anchorRect);

    useEffect(() => {
        if (!anchorSelector) return undefined;
        const findElement = () => {
            const el = document.querySelector(anchorSelector);
            if (el) setRect(el.getBoundingClientRect());
        };
        findElement();
        const interval = setInterval(findElement, 1000);
        window.addEventListener('resize', findElement);
        return () => {
            clearInterval(interval);
            window.removeEventListener('resize', findElement);
        };
    }, [anchorSelector]);

    const effectiveRect = anchorRect || rect;
    if (!effectiveRect) return null;

    const handleClose = (e) => {
        e.stopPropagation();
        if (onDismiss) onDismiss();
    };

    // 'left' opens the bubble to the LEFT of the anchor (arrow on the right edge);
    // 'right' (default) keeps the original behaviour (arrow on the left edge).
    const isLeft = placement === 'left';
    const leftPos = isLeft
        ? Math.max(8, effectiveRect.left - width - offset)
        : effectiveRect.right + offset;
    const arrowStyles = isLeft
        ? { right: '-10px', borderLeft: '8px solid #052F44' }
        : { left: '-10px', borderRight: '8px solid #052F44' };

    return (
        <Box
            onClick={dismissOnClick ? onDismiss : undefined}
            sx={{
                position: 'fixed',
                top: effectiveRect.top,
                left: leftPos,
                zIndex,
                width,
                background: 'linear-gradient(135deg, #052F44 0%, #0a4a6e 100%)',
                color: '#ffffff',
                p: 2.5,
                borderRadius: '12px',
                boxShadow: '0 8px 32px rgba(5,47,68,0.3)',
                cursor: dismissOnClick ? 'pointer' : 'default',
                animation: 'slide-in-right 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                borderLeft: '4px solid #FF6900',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: '18px',
                    width: 0,
                    height: 0,
                    borderTop: '8px solid transparent',
                    borderBottom: '8px solid transparent',
                    ...arrowStyles,
                },
                '@keyframes slide-in-right': {
                    '0%': { opacity: 0, transform: 'translateX(-10px)' },
                    '100%': { opacity: 1, transform: 'translateX(0)' },
                },
                ...sx,
            }}
        >
            {showClose && onDismiss && (
                <IconButton
                    onClick={handleClose}
                    size="small"
                    aria-label="Close"
                    sx={{
                        position: 'absolute', top: 4, right: 4, p: '2px',
                        color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' },
                    }}
                >
                    <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
            )}
            {typeof children === 'string' ? (
                <Typography variant="body2" sx={{ lineHeight: 1.5, opacity: 0.9, fontSize: '13px', pr: showClose ? 2 : 0 }}>
                    {children}
                </Typography>
            ) : children}
        </Box>
    );
};

export default FloatingNudge;
